import {Container, Graphics, Sprite, Text, Texture} from 'pixi.js';
import {gsap} from 'gsap';

/**
 * Avatar impacts: every counted snap bursts the shooter's avatar into the
 * named cell — the avatar itself is the hit marker (replaces the classic
 * bullet-hit). A hit gets a gold ring and lingers; a miss fades out.
 */

const textureCache = new Map();
const pdsCache = new Map();

/** Public-https gate for attacker-influenced URLs (did docs live under
 *  user control): https only, no credentials, no localhost/private/CGNAT
 *  IPv4, no bracketed IPv6 literals, no .local. Parsed URL or null. */
function publicHttps(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase().replace(/\.$/, ''); // 'localhost.' is still localhost
    const nonPublicV4 = /^(localhost$|127\.|10\.|0\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.)/;
    if (u.protocol !== 'https:' || u.username || u.password ||
        nonPublicV4.test(host) || host.startsWith('[') ||
        host.endsWith('.local') || host.endsWith('.localhost')) {
      return null;
    }
    return u;
  } catch (e) {
    return null;
  }
}

/** did → PDS base URL (plc.directory / did:web well-known, both CORS-open). */
async function pdsFor(did) {
  if (!pdsCache.has(did)) {
    pdsCache.set(did, (async () => {
      const docUrl = did.startsWith('did:web:')
        ? `https://${did.slice('did:web:'.length).split(':').map(decodeURIComponent).join('/')}/.well-known/did.json`
        : `https://plc.directory/${did}`;
      if (!publicHttps(docUrl)) {
        return null; // a did:web aimed anywhere non-public stops HERE
      }
      const doc = await (await fetch(docUrl,
        {signal: AbortSignal.timeout(4000), redirect: 'error'})).json();
      const svc = (doc.service || []).find((s) => s.id === '#atproto_pds' || s.id === `${did}#atproto_pds`);
      if (!svc || !publicHttps(svc.serviceEndpoint)) {
        return null;
      }
      return svc.serviceEndpoint.replace(/\/$/, '');
    })().catch(() => null));
    // a failed/absent PDS must not be cached forever — evict null results
    // after settling so a later avatar render can retry
    void pdsCache.get(did).then((v) => {
      if (v == null) {
        pdsCache.delete(did);
      }
    });
  }
  return pdsCache.get(did);
}

/**
 * Fetch an avatar into a Pixi texture; null on any failure (fallback drawn).
 * cdn.bsky.app sends no CORS headers, so WebGL can't use it directly — but the
 * CDN URL carries the did + blob cid, and the user's own PDS serves that same
 * blob CORS-open via com.atproto.sync.getBlob. First-party, straight from
 * their repo.
 */
export function avatarTexture(url) {
  if (!url) {
    return Promise.resolve(null);
  }
  if (!textureCache.has(url)) {
    textureCache.set(url, (async () => {
      try {
        const m = url.match(/\/img\/[^/]+\/plain\/(did:[^/]+)\/([a-z0-9]+)(@\w+)?$/i);
        if (!m) {
          throw new Error('unrecognized avatar url');
        }
        const [, did, cid] = m;
        const pds = await pdsFor(did);
        if (!pds) {
          throw new Error('no pds');
        }
        const res = await fetch(`${pds}/xrpc/com.atproto.sync.getBlob?did=${encodeURIComponent(did)}&cid=${cid}`,
          {signal: AbortSignal.timeout(6000), redirect: 'error'});
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        const bitmap = await createImageBitmap(await res.blob());
        return Texture.from(bitmap);
      } catch (e) {
        textureCache.delete(url); // transient failure — let a later call retry
        return null;
      }
    })());
  }
  return textureCache.get(url);
}

const LABEL_STYLE = {
  fontFamily: 'Courier New, monospace',
  fontSize: 15,
  fontWeight: '700',
  fill: 0xffffff,
  stroke: {color: 0x000000, width: 4}
};

/** Circular avatar (or lettered fallback) of radius r, centered on 0,0. */
export function avatarDisc(texture, handle, r) {
  const disc = new Container();
  if (texture) {
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    const scale = (r * 2) / Math.min(sprite.width, sprite.height);
    sprite.scale.set(scale);
    const mask = new Graphics().circle(0, 0, r).fill(0xffffff);
    disc.addChild(mask);
    sprite.mask = mask;
    disc.addChild(sprite);
  } else {
    disc.addChild(new Graphics().circle(0, 0, r).fill(0x1d1d24));
    const initial = new Text({
      text: (String(handle || '')[0] || '?').toUpperCase(),
      style: {fontFamily: 'Courier New, monospace', fontSize: r, fontWeight: '700', fill: 0xdddde2}
    });
    initial.anchor.set(0.5);
    disc.addChild(initial);
  }
  return disc;
}

/**
 * makeImpact
 * The BROKEN AVATAR, for real this time: the avatar image itself shatters
 * into irregular wedge shards that separate slightly, with dark seams showing
 * in the gaps — like a dropped photograph. No painted lines, no rays; the
 * break is made of the avatar's own pieces, so it also fades cleanly.
 * A miss fades after a beat; a hit is bigger and stays until the round ends.
 */
export function makeImpact({texture, handle, hit}) {
  const impact = new Container();
  const r = hit ? 38 : 28;

  if (texture) {
    // dark backing disc — the gaps between displaced shards read as seams
    impact.addChild(new Graphics().circle(0, 0, r).fill({color: 0x000000, alpha: 0.55}));

    const ix = -r * 0.18;
    const iy = -r * 0.1;
    const shards = 5;
    const angles = [];
    for (let i = 0; i <= shards; i++) {
      angles.push((i / shards) * Math.PI * 2 + 0.5 + (i % 2 ? 0.22 : -0.14));
    }
    const spread = hit ? 3.2 : 2.2; // how far the pieces slide apart
    for (let i = 0; i < shards; i++) {
      const shard = new Container();
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const scale = (r * 2) / Math.min(sprite.width, sprite.height);
      sprite.scale.set(scale);

      // wedge from the impact point out to the rim arc
      const wedge = new Graphics();
      wedge.moveTo(ix, iy);
      const steps = 4;
      for (let s = 0; s <= steps; s++) {
        const a = angles[i] + (angles[i + 1] - angles[i]) * (s / steps);
        wedge.lineTo(Math.cos(a) * r * 1.02, Math.sin(a) * r * 1.02);
      }
      wedge.closePath().fill(0xffffff);
      shard.addChild(wedge);
      sprite.mask = wedge;
      shard.addChild(sprite);

      const mid = (angles[i] + angles[i + 1]) / 2;
      shard.position.set(Math.cos(mid) * (spread + (i % 2) * 1.6), Math.sin(mid) * (spread + (i % 2) * 1.6));
      shard.rotation = i % 2 ? 0.045 : -0.04;
      impact.addChild(shard);
    }
  } else {
    impact.addChild(avatarDisc(null, handle, r));
  }

  const label = new Text({text: '@' + handle, style: LABEL_STYLE});
  label.anchor.set(0.5, 0);
  label.position.set(0, r + 9);
  if (hit) {
    label.style.fill = 0xffc73b;
  }
  impact.addChild(label);

  // lands with a thud and a slight tilt
  impact.rotation = -0.12;
  impact.scale.set(0.25);
  gsap.to(impact.scale, {x: 1, y: 1, duration: 0.24, ease: 'back.out(2)'});
  gsap.to(impact, {rotation: -0.05, duration: 0.35, ease: 'power2.out'});
  if (!hit) {
    gsap.to(impact, {alpha: 0, delay: 1.5, duration: 0.45, onComplete: () => {
      impact.parent && impact.parent.removeChild(impact);
    }});
  }
  return impact;
}

/** Small floating notice, e.g. "@handle is out of ammo". */
export function makeNotice(text) {
  const notice = new Text({text, style: LABEL_STYLE});
  notice.anchor.set(0.5);
  gsap.to(notice, {alpha: 0, delay: 1.6, duration: 0.5, onComplete: () => {
    notice.parent && notice.parent.removeChild(notice);
  }});
  return notice;
}
